import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, DataTable, EmptyState, EntitlementDeniedState, ErrorState, Field, LoadingState, Select, TextInput, formatDate } from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import type { Meeting, MeetingState } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { meetingApi } from "../../lib/api";
import { DemoNotice, meetingLabel, stateTone } from "./MeetingShared";
import { CreateMeeting } from "./MeetingActions";

export function MeetingHome() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [stateFilter, setStateFilter] = useState<MeetingState | "">("");
  const [search, setSearch] = useState("");
  const request = useAsync(
    () => meetingApi.meetings(orgId, {
      ...(stateFilter ? { state: stateFilter } : {}),
      ...(search ? { search } : {}),
    }),
    [orgId, search, stateFilter],
  );
  const meetings = request.data?.data.meetings ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Meeting AI</h1>
          <p>The governed record of what your business decided and committed to.</p>
        </div>
        <div className="ods-row">
          <CreateMeeting orgId={orgId} onCreated={request.reload} />
          <Link to="/studio/meeting-ai/commitments">
            <Button size="sm" variant="secondary">Commitments</Button>
          </Link>
          <Link to="/studio/meeting-ai/work-items">
            <Button size="sm">Work items</Button>
          </Link>
        </div>
      </div>
      {request.data && <DemoNotice demoMode={request.data.demoMode} />}
      <Card>
        <CardHeader title="Meetings" />
        <CardBody>
          <div className="ods-row" style={{ alignItems: "end", marginBottom: "var(--ods-space-3)" }}>
            <Field label="State">
              {(id) => (
                <Select id={id} value={stateFilter} onChange={(event) => setStateFilter(event.target.value as MeetingState | "")}>
                  <option value="">All states</option>
                  {(["DRAFT", "IN_REVIEW", "ADOPTED", "SEALED"] as MeetingState[]).map((state) => (
                    <option key={state} value={state}>{meetingLabel(state)}</option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Search">
              {(id) => <TextInput id={id} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Meeting title" />}
            </Field>
          </div>
          {meetings.length > 0 && <p>Showing {meetings.length} {meetings.length === 1 ? "meeting" : "meetings"} in the current filtered view.</p>}
          {request.loading && <LoadingState label="Loading meetings…" />}
          {request.error instanceof ApiError && request.error.code === "entitlement_denied"
            ? <EntitlementDeniedState product="Meeting AI" />
            : request.error && <ErrorState description={request.error.message} onRetry={request.reload} />}
          {request.data && meetings.length === 0 && <EmptyState title="No meetings" description="No governed meetings match these filters." />}
          {meetings.length > 0 && (
            <DataTable
              caption="Meetings"
              rows={meetings}
              columns={[
                { key: "title", header: "Meeting", render: (meeting: Meeting) => <Link to={`/studio/meeting-ai/meetings/${meeting.id}`}>{meeting.title}</Link> },
                { key: "state", header: "Status", render: (meeting: Meeting) => <Badge tone={stateTone(meeting.state)}>{meetingLabel(meeting.state)}</Badge> },
                { key: "scheduledStart", header: "Date", render: (meeting: Meeting) => formatDate(meeting.scheduledStart) },
                { key: "createdAt", header: "Recorded", render: (meeting: Meeting) => formatDate(meeting.createdAt) },
              ]}
            />
          )}
        </CardBody>
      </Card>
    </>
  );
}
